import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import Seo from "../components/Seo.tsx";
import { Field, Input } from "../components/ui/Field.tsx";
import { Button } from "../components/ui/Button.tsx";
import Divider from "../components/ornaments/Divider.tsx";
import Feather from "../components/ornaments/Feather.tsx";
import { createCreation, ApiError } from "../lib/api.ts";

const TODAY = new Date().toISOString().slice(0, 10);

export default function Create() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit =
    name.trim().length > 0 &&
    birthDate.length === 10 &&
    emailLooksValid &&
    !submitting;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const { slug } = await createCreation({
        name: name.trim(),
        birthDate,
        email: email.trim(),
      });
      navigate(`/creation/${slug}`);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Something went quiet in the apothecary. Please try again.",
      );
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-20 md:px-10 md:py-28">
      <Seo
        title="Compose Your Horoscope Candle"
        description="Tell us your name and birthday. We'll draw your zodiac sign and compose a personal candle — a private reading, a scent story, and a dedication for the jar."
        path="/create"
      />
      <section className="marble-panel relative overflow-hidden px-8 py-20 md:px-16 md:py-28">
        <Feather
          className="pointer-events-none absolute top-1/2 left-1/2 h-[560px] w-auto -translate-x-1/2 -translate-y-1/2 opacity-[0.07]"
          style={{ animation: "var(--animate-fade-in)" }}
        />
        <Feather
          className="pointer-events-none absolute top-8 right-8 hidden h-32 w-auto rotate-[18deg] opacity-60 md:block"
          style={{
            animation: "var(--animate-fade-up)",
            animationDelay: "300ms",
          }}
        />

        <div className="relative">
          <div
            className="text-center"
            style={{ animation: "var(--animate-fade-up)" }}
          >
            <span className="eyebrow">A Candle of Your Own</span>
            <h1 className="mt-6 font-serif text-5xl leading-[1.05] text-[var(--color-emerald-deep)] md:text-6xl">
              Compose your{" "}
              <em className="text-gold-leaf font-serif italic">candle.</em>
            </h1>
            <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-[var(--color-charcoal)]/75">
              Tell us your name and the day you were born. We will draw your
              sign and compose a private candle from it — a personal reading,
              a scent story, a dedication for the jar.
            </p>
          </div>

          <Divider className="my-12" />

          <form
            onSubmit={onSubmit}
            className="mx-auto flex max-w-md flex-col gap-10"
            style={{
              animation: "var(--animate-fade-up)",
              animationDelay: "180ms",
            }}
          >
            <Field label="Your first name" htmlFor="name">
              <Input
                id="name"
                name="name"
                type="text"
                autoComplete="given-name"
                placeholder="e.g. Liana"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={64}
                disabled={submitting}
              />
            </Field>

            <Field
              label="The day you were born"
              htmlFor="birthDate"
              hint="Used only to draw your zodiac sign."
            >
              <Input
                id="birthDate"
                name="birthDate"
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                required
                max={TODAY}
                disabled={submitting}
              />
            </Field>

            <Field
              label="Where shall we send your candle?"
              htmlFor="email"
              hint="Added to BeAdoré's private letter list — unsubscribe any time."
            >
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@somewhere.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={254}
                disabled={submitting}
              />
            </Field>

            {error ? (
              <p className="text-center text-sm italic text-[var(--color-oxblood)]">
                {error}
              </p>
            ) : null}

            <div className="flex justify-center pt-4">
              <Button type="submit" disabled={!canSubmit}>
                {submitting ? "Composing…" : "Compose My Candle"}
              </Button>
            </div>

            {submitting ? (
              <p className="text-center text-xs uppercase tracking-[0.18em] text-[var(--color-stone)]">
                drawing your sign · pressing the wax · steeping the notes
              </p>
            ) : null}
          </form>
        </div>
      </section>
    </div>
  );
}
